import { useTranslation } from 'react-i18next';
import { AlertTriangle, Bell, CalendarRange, Clock, TrendingUp } from 'lucide-react';
import { KpiCard } from '@/components/shared/KpiCard';
import { useCurrency } from '@/hooks/useCurrency';
import type { ForecastReviewKpis } from '@/services/forecastReviewService';

interface ForecastKpiCardsProps {
  kpis: ForecastReviewKpis | null;
  loading?: boolean;
  viewType: 'units' | 'value';
  granularity: 'week' | 'month';
}

function formatVariation(v: number | null | undefined): string {
  if (v == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${(v * 100).toFixed(1)}%`;
}

function formatPeriodLabel(period: string | undefined, granularity: 'week' | 'month'): string {
  if (!period) return '—';
  if (granularity === 'week' && period.includes('-W')) {
    const [y, w] = period.split('-W');
    return `S${Number(w)} ${y}`;
  }
  const [year, month] = period.split('-');
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const mi = Number(month) - 1;
  if (mi < 0 || mi > 11) return period;
  return `${months[mi]} ${year}`;
}

function formatAmountForHint(
  v: number | null | undefined,
  viewType: 'units' | 'value',
  formatViewAmount: (value: number | null | undefined, viewType: 'units' | 'value') => string,
): string {
  if (v == null) return '—';
  return formatViewAmount(v, viewType);
}

function variationVariant(v: number | null | undefined): 'default' | 'warning' | 'danger' {
  if (v == null) return 'default';
  if (Math.abs(v) >= 0.2) return 'danger';
  if (Math.abs(v) >= 0.1) return 'warning';
  return 'default';
}

const hintSubtitleClass = 'mt-2 max-w-[14rem] text-center text-xs leading-snug text-gray-500';

export function ForecastKpiCards({ kpis, loading, viewType, granularity }: ForecastKpiCardsProps) {
  const { t } = useTranslation();
  const { formatViewAmount, symbol } = useCurrency();

  const horizonCount = kpis?.horizon_periods ?? 1;

  const periodUnit =
    granularity === 'week'
      ? t('forecastReview.kpis.periodUnitWeek')
      : t('forecastReview.kpis.periodUnitMonth');
  const periodUnitLabel =
    horizonCount === 1
      ? periodUnit
      : granularity === 'week'
        ? t('forecastReview.kpis.periodUnitWeekPlural')
        : t('forecastReview.kpis.periodUnitMonthPlural');

  const forecastPeriodLabel =
    kpis?.forecast_period_label ??
    formatPeriodLabel(kpis?.forecast_period_end ?? kpis?.forecast_period, granularity);
  const lyPeriodLabel =
    kpis?.comparison_last_year_period_label ??
    formatPeriodLabel(kpis?.comparison_last_year_period, granularity);
  const recentPeriodLabel =
    kpis?.comparison_recent_period_label ??
    formatPeriodLabel(kpis?.comparison_recent_period, granularity);

  const forecastTotalHint = t('forecastReview.kpis.forecastTotalHint', {
    period: forecastPeriodLabel,
    count: horizonCount,
    unitLabel: periodUnitLabel,
  });
  const monthlyAvg = kpis?.forecast_monthly_average;
  const forecastSubtitle =
    monthlyAvg != null
      ? `${forecastTotalHint}. ${t('forecastReview.kpis.forecastMonthlyAvgHint', {
          monthly: formatViewAmount(monthlyAvg, 'units'),
        })}`
      : forecastTotalHint;

  const varAaHint = t('forecastReview.kpis.varLastYearHint', {
    forecastPeriod: forecastPeriodLabel,
    refPeriod: lyPeriodLabel,
    refTotal: formatAmountForHint(kpis?.comparison_last_year_total, viewType, formatViewAmount),
  });

  const varRecentHint = t('forecastReview.kpis.varRecentHint', {
    forecastPeriod: forecastPeriodLabel,
    refPeriod: recentPeriodLabel,
    refTotal: formatAmountForHint(kpis?.comparison_recent_total, viewType, formatViewAmount),
  });

  const variationMetricNote =
    viewType === 'units'
      ? t('forecastReview.kpis.metricBasisUnits')
      : t('forecastReview.kpis.metricBasisValue', { currencySymbol: symbol });

  const openAlerts = kpis?.open_alerts_count ?? 0;
  const criticalAlerts = kpis?.critical_alerts_count ?? 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <KpiCard
        title={t('forecastReview.kpis.forecastTotal')}
        value={kpis?.forecast_total_quantity ?? '—'}
        isCount
        secondaryValue={kpis?.forecast_total_value ?? null}
        secondaryIsEuro
        icon={<TrendingUp className="h-5 w-5 text-[#1681ba]" />}
        subtitle={forecastSubtitle}
        subtitleClassName={hintSubtitleClass}
        loading={loading}
      />
      <KpiCard
        title={t('forecastReview.kpis.varLastYear')}
        value={formatVariation(kpis?.variation_vs_last_year)}
        variant={variationVariant(kpis?.variation_vs_last_year)}
        valueNote={variationMetricNote}
        icon={<CalendarRange className="h-5 w-5 text-violet-500" />}
        subtitle={varAaHint}
        subtitleClassName={hintSubtitleClass}
        loading={loading}
      />
      <KpiCard
        title={t('forecastReview.kpis.varRecent')}
        value={formatVariation(kpis?.variation_vs_recent_period)}
        variant={variationVariant(kpis?.variation_vs_recent_period)}
        valueNote={variationMetricNote}
        icon={<Clock className="h-5 w-5 text-amber-500" />}
        subtitle={varRecentHint}
        subtitleClassName={hintSubtitleClass}
        loading={loading}
      />
      <KpiCard
        title={t('forecastReview.kpis.openAlerts')}
        value={openAlerts}
        isCount
        variant={openAlerts > 0 ? 'warning' : 'default'}
        icon={<Bell className="h-5 w-5 text-orange-500" />}
        loading={loading}
      />
      <KpiCard
        title={t('forecastReview.kpis.criticalAlerts')}
        value={criticalAlerts}
        isCount
        variant={criticalAlerts > 0 ? 'danger' : 'default'}
        icon={<AlertTriangle className="h-5 w-5 text-red-500" />}
        loading={loading}
      />
    </div>
  );
}
